import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../shared/auth/AuthContext";
import { useI18n } from "../shared/i18n/I18nContext";
import { Alert } from "../shared/ui/Alert";
import { cx } from "../shared/ui/classNames";
import { PublicLayout } from "../shared/ui/PublicLayout";
import styles from "../shared/ui/ui.module.css";

/**
 * Completes the OIDC login after the redirect back and opens the requested page. Both the progress
 * and a failure are public screens with heading and document title (UX-120); a failure explains
 * likely causes and offers a new sign-in as the one action.
 *
 * @returns The callback page.
 */
export function LoginCallbackPage(): JSX.Element {
  const { service } = useAuth();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const started = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // The authorization code can be redeemed only once (StrictMode runs effects twice).
    if (started.current) {
      return;
    }

    started.current = true;
    service.completeLogin(window.location.href).then(
      (returnTo) => {
        void navigate(returnTo, { replace: true });
      },
      () => {
        setFailed(true);
      },
    );
  }, [service, navigate]);

  if (!failed) {
    const completing = (
      <PublicLayout title={t("auth.signIn")} look="hero">
        <p role="status">{t("auth.completing")}</p>
      </PublicLayout>
    );
    return completing;
  }

  const failure = (
    <PublicLayout title={t("auth.failedTitle")} look="signIn">
      <Alert tone="danger">{t("auth.failedHint")}</Alert>
      <div>
        <button
          type="button"
          className={cx(styles["button"], styles["primary"])}
          onClick={() => {
            void service.login("/", locale);
          }}
        >
          {t("auth.retry")}
        </button>
      </div>
    </PublicLayout>
  );

  return failure;
}
